import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";

import type {
	SharedUserFilterState,
	UserFilterItem,
	UserFilterParamType,
} from "~/shared/state/user-filter-state.ts";
import { sharedUserFilterAtom } from "~/shared/state/user-filter-state.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

/** Minimal shape required from a user-filter config entry. */
type FilterConfig = { id: number; paramType: string };

type Options = {
	/**
	 * Optional one-shot seed used when the atom is still `null` on first mount.
	 * Return `null` to fall back to the default seed (current employee under
	 * `ltlBrokers`). Useful for pages that want to honor URL params or
	 * sessionStorage values on initial load (e.g. Loads).
	 */
	initialSeed?: () => SharedUserFilterState | null;
	/**
	 * When `false`, the hook skips hydration and atom writes (handlers become
	 * no-ops). Use for pages that mount the shared filter UI only in certain
	 * deployment modes (e.g. Loads renders the filter for internal users only).
	 * Defaults to `true`.
	 */
	enabled?: boolean;
};

/**
 * Reads/writes the cross-page user filter selection (LTL AR / LAE / Customer)
 * stored in `sharedUserFilterAtom`. Centralizes hydration, derivation of ID
 * arrays, and the change/select-filter handlers used by AR Dashboard, Loads
 * (Shipment Board), and Quote Board.
 *
 * @param filters - The page's user-filter config array (used to resolve the
 *   currently-selected entry by `paramType`).
 * @param options - Optional hydration seed override.
 */
export const useSharedUserFilter = <F extends FilterConfig>(
	filters: readonly F[],
	options: Options = {},
) => {
	const { fullName, employeeId } = useIsAuthenticated();
	const [sharedUserFilter, setSharedUserFilter] = useAtom(sharedUserFilterAtom);

	const { initialSeed, enabled = true } = options;

	// One-shot hydration when the atom is empty. Subsequent local changes
	// must not re-seed, hence the empty deps array.
	useEffect(() => {
		if (!enabled) return;
		if (sharedUserFilter !== null) return;
		const seed = initialSeed?.();
		if (seed) {
			setSharedUserFilter(seed);
			return;
		}
		setSharedUserFilter({
			paramType: "ltlBrokers",
			value: employeeId ? [{ id: Number(employeeId), name: fullName }] : [],
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: hydration only runs once when atom is empty
	}, []);

	const selectedFilter = useMemo(
		() => filters.find((f) => f.paramType === sharedUserFilter?.paramType),
		[filters, sharedUserFilter?.paramType],
	);

	const value = useMemo(
		() => sharedUserFilter?.value ?? [],
		[sharedUserFilter?.value],
	);

	const ltlBrokerIds = useMemo(
		() =>
			sharedUserFilter?.paramType === "ltlBrokers"
				? sharedUserFilter.value.map((v) => Number(v.id))
				: [],
		[sharedUserFilter],
	);

	const primaryBrokerIds = useMemo(
		() =>
			sharedUserFilter?.paramType === "laes"
				? sharedUserFilter.value.map((v) => Number(v.id))
				: [],
		[sharedUserFilter],
	);

	const customerIds = useMemo(
		() =>
			sharedUserFilter?.paramType === "customers"
				? sharedUserFilter.value.map((v) => Number(v.id))
				: [],
		[sharedUserFilter],
	);

	const selectedCustomers = useMemo(
		() =>
			sharedUserFilter?.paramType === "customers"
				? sharedUserFilter.value.map((v) => ({
						id: Number(v.id),
						name: v.name,
					}))
				: [],
		[sharedUserFilter],
	);

	const handleChange = useCallback(
		(e: { value: UserFilterItem[] }) => {
			setSharedUserFilter((prev) => ({
				paramType: prev?.paramType ?? "ltlBrokers",
				value: e.value,
			}));
		},
		[setSharedUserFilter],
	);

	const handleSelectFilter = useCallback(
		({ value: nextFilter }: { value: FilterConfig }) => {
			setSharedUserFilter({
				paramType:
					nextFilter.id > 0
						? (nextFilter.paramType as UserFilterParamType)
						: "ltlBrokers",
				value: [],
			});
		},
		[setSharedUserFilter],
	);

	return {
		selectedFilter,
		value,
		ltlBrokerIds,
		primaryBrokerIds,
		customerIds,
		selectedCustomers,
		handleChange,
		handleSelectFilter,
		setSharedUserFilter,
	};
};
